<template>
  <div>
    <div class="container mx-auto">
      <empty
        v-if="loaded && wheels.length === 0"
        icon="dharmachakra"
        title="No wheels"
        message="To create a wheel, nothing could be easier, just click on the following link:"
      >
      </empty>

      <div v-else-if="loaded && wheels.length > 0" class="-mx-4 sm:-mx-8 px-4 sm:px-8 py-4 overflow-x-auto">
        <div class="inline-block min-w-full shadow rounded-lg overflow-hidden">
          <table class="min-w-full leading-normal">
            <thead>
              <tr>
                <th
                  class="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider"
                >
                  {{ $t('wheel.list.name') }}
                </th>
                <th
                  class="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider"
                >
                  <el-tooltip
                    effect="dark"
                    content="To test the wheel, send header X-Wheel-Code: XXX and X-Lang: fr"
                    placement="top"
                    ><span class="cursor-pointer">
                      {{ $t('wheel.list.code') }}
                    </span></el-tooltip
                  >
                </th>
                <th
                  class="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider"
                >
                  {{ $t('wheel.list.status') }}
                </th>
                <th
                  class="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider"
                >
                  {{ $t('wheel.list.where') }}
                </th>
                <th
                  class="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider"
                >
                  {{ $t('wheel.list.date') }}
                </th>
                <th
                  class="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider"
                >
                  <el-tooltip effect="dark" content="Unique Wheel Views" placement="top"
                    ><span class="cursor-pointer">
                      {{ $t('wheel.list.stats') }}
                    </span></el-tooltip
                  >
                </th>
                <th
                  class="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider"
                ></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(wheel, index) in wheels" :key="wheel.id">
                <td class="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                  <p class="text-gray-900 whitespace-no-wrap">{{ wheel.name }}</p>
                  <p class="text-xs">{{ new Date(wheel.datecreation).toLocaleDateString('fr') }}</p>
                </td>

                <td class="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                  <div class="fields-container">
                    <div class="relative">
                      <button
                        class="rounded bg-gray-200 button-field font-semibold"
                        v-on:click="
                          e => {
                            copyFieldToClipBoard(e, wheel.code)
                          }
                        "
                      >
                        {{ wheel.code }}
                      </button>
                      <div class="copy-popin">Copied !</div>
                    </div>
                  </div>
                </td>

                <td class="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                  <span
                    class="relative inline-block px-3 py-1 font-semibold leading-tight"
                    :class="{
                      'text-green-900':
                        wheel.status === 'enable' || wheel.status === 'pending' || wheel.status === 'terminated',
                      'text-gray-900': wheel.status === 'disable'
                    }"
                  >
                    <span
                      aria-hidden
                      class="absolute inset-0 opacity-50 rounded"
                      :class="{
                        'bg-green-200':
                          wheel.status === 'enable' || wheel.status === 'pending' || wheel.status === 'terminated',
                        'bg-gray-200': wheel.status === 'disable'
                      }"
                    ></span>
                    <span class="relative">
                      <template v-if="wheel.status === 'enable'">Enable</template>
                      <template v-else-if="wheel.status === 'pending'">Pending</template>
                      <template v-else-if="wheel.status === 'terminated'">Terminated</template>
                      <template v-else>Disable</template>
                    </span>
                  </span>
                </td>

                <td class="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                  <p class="text-gray-900 whitespace-no-wrap">
                    <template v-if="wheel.url_target_list.includes('.*')"
                      >Every where on <br />{{ wheel.url }}</template
                    >
                    <template v-else>
                      <span :key="index" class="relative inline-block px-3 py-1 text-gray-900 leading-tight">
                        <span aria-hidden class="absolute inset-0 bg-gray-200 opacity-50 rounded mr-2"></span>
                        <span class="relative">{{ wheel.url_target_string }}</span>
                      </span>
                      on <br />{{ wheel.url }}</template
                    >
                  </p>
                </td>

                <td class="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                  <p class="text-gray-900 whitespace-no-wrap">
                    <span v-if="wheel.frequencylimitation === 'none' && wheel.frequencylimitationvalue === 1"
                      >Show every time</span
                    >
                    <span v-if="wheel.frequencylimitation === 'none' && wheel.frequencylimitationvalue === 0"
                      >Only manually</span
                    >
                    <span v-if="wheel.frequencylimitation !== 'none' && wheel.frequencylimitationvalue > 0"
                      >Show {{ wheel.frequencylimitationvalue }} times per {{ wheel.frequencylimitation }}</span
                    >
                    <span v-if="wheel.frequencylimitation !== 'none' && wheel.frequencylimitationvalue === 0"
                      >Can't show</span
                    >

                    <span v-if="wheel.activationdate && wheel.deactivationdate"
                      >between {{ _getDate(wheel.activationdate, site.timezone) | date }} and
                      {{ _getDate(wheel.deactivationdate, site.timezone) | date }}</span
                    >
                  </p>
                </td>

                <td class="px-5 py-5 border-b border-gray-200 bg-white text-center">
                  <div class="text-gray-900 whitespace-no-wrap">
                    <button @click="openPopup(index)">
                      <span>{{ getUniqueViews(index) }}</span>
                      <font-awesome-icon icon="eye"></font-awesome-icon>
                    </button>
                  </div>
                </td>

                <td class="px-5 py-5 border-b border-gray-200 bg-white text-sm text-right">
                  <router-link
                    class="cursor-pointer text-gray-600 bg-gray-200 rounded p-2"
                    :to="{ name: 'Wheel', params: { site: site.sitekey, id: wheel.id } }"
                  >
                    <font-awesome-icon icon="pencil-alt"></font-awesome-icon>
                  </router-link>
                  <button
                    class="cursor-pointer text-gray-600 bg-gray-200 rounded p-2"
                    @click="duplicateWheel(wheel.id)"
                  >
                    <font-awesome-icon icon="clone"></font-awesome-icon>
                  </button>
                  <button class="cursor-pointer text-gray-600 bg-gray-200 rounded p-2" @click="confirmDeletion(wheel)">
                    <font-awesome-icon icon="trash"></font-awesome-icon>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <modal-confirmation
        v-if="deleteWheelConfirmation"
        :open="deleteWheelConfirmation"
        type="danger"
        messageTitle="Warnings !"
        message="Do you really want to remove this wheel ? This actions cannot be undone."
        messageAction="Yes, I'm sure !"
        @execute="deleteWheel()"
        @close="deleteWheelConfirmation = false"
      />

      <div v-if="loaded && wheels.length > 0" class="text-right px-2">
        <p v-if="wheels.length > 1">{{ wheels.length }} results</p>
        <p v-else>{{ wheels.length }} result</p>
      </div>

      <wheels-ui-modal-stats
        v-if="isModalStatsOpen"
        :open="isModalStatsOpen"
        :wheel="wheelOpen"
        :stats="stats.find(stat => stat.wheelId === wheelOpen.id)"
        @close="isModalStatsOpen = false"
      />
    </div>
  </div>
</template>

<script>
import Empty from '@/components/box-entity-empty.vue'
import ModalConfirmation from '@/components/others/utils/ModalConfirmation.vue'
import { mapActions, mapGetters } from 'vuex'
import { getStats } from '../../../services/wheel.api'

export default {
  name: 'WheelUiList',

  components: { Empty, ModalConfirmation },

  props: {},

  data() {
    return {
      loaded: false,
      viewsUnique: [],
      isModalStatsOpen: false,
      wheelOpen: null,
      wheels: [],
      wheelToDelete: null,
      deleteWheelConfirmation: false,
      stats: []
    }
  },
  watch: {
    site: {
      handler: 'init',
      immediate: true
    }
  },
  computed: {
    ...mapGetters('wheel', {
      allWheels: 'wheels'
    }),
    ...mapGetters('account', {
      site: 'currentSite',
      lang: 'defaultLang'
    })
  },
  methods: {
    ...mapActions('gifts', ['addGift', 'loadGifts']),
    ...mapActions('wheel', ['loadWheels', 'updateWheel', 'addWheel', 'removeWheel']),

    copyFieldToClipBoard(e, field) {
      navigator.clipboard.writeText(field)
      e.target.classList.add('active')
      setTimeout(() => {
        e.target.classList.remove('active')
      }, 1000)
    },

    openPopup(index) {
      this.wheelOpen = this.wheels[index]
      this.isModalStatsOpen = true
    },
    init() {
      this.loadWheels().then(() => {
        this.loaded = true
        this.wheels = this.allWheels
        this.loadWheelStats()
        this.getUrlTargetToString(this.wheels)
      })
    },
    async loadWheelStats() {
      try {
        const newStats = []
        for (const wheel of this.wheels) {
          let wheelStats = await getStats(this.site.sitekey, wheel.id)
          newStats.push({
            wheelId: wheel.id,
            wheelStats
          })
        }
        this.stats = newStats
      } catch (error) {
        console.error(error)
      }
    },
    getUniqueViews(index) {
      if (this.stats[index] && this.stats[index].wheelStats) {
        return this.stats[index].wheelStats.length
      }
      return 0
    },
    getUrlTargetToString(wheels) {
      for (let wheel of wheels) {
        let urlString = ""
        wheel.url_target_list.forEach((path, index) => {
          if (index === 0) {
            urlString += path
          } else {
            urlString += ', ' + path
          }
        })
        wheel.url_target_string = urlString;
      }
    },

    confirmDeletion(wheel) {
      this.wheelToDelete = wheel
      this.deleteWheelConfirmation = true
    },
    deleteWheel() {
      this.removeWheel(this.wheelToDelete)
      this.wheelToDelete = null
      this.deleteWheelConfirmation = false
      this.init()
    },
    duplicateWheel(idWheel) {
      let wheel = this.wheels.find(w => w.id === idWheel)
      let wheelClone = Object.assign({}, wheel)

      wheelClone.status = 'disable'
      wheelClone.name = 'Copy of ' + wheelClone.name

      this.addWheel({ ...wheelClone })
        .then(newWheel => {
          wheelClone.code = newWheel.code
          wheelClone.id = newWheel.id
          this.updateWheel({ ...wheelClone })
            .then(upNewWheel => {
              this.loadGifts(idWheel)
                .then(gifts => {
                  for (let index in gifts) {
                    this.addGift({ wheelId: upNewWheel.id, gift: gifts.at(index) })
                  }
                })
                .catch(response => {
                  this.$notify.error({
                    title: this.$root.$t('api.' + response.data.code + '.title'),
                    message: this.$root.$t('api.' + response.data.code + '.message')
                  })
                })
              this.$notify({
                title: 'Success',
                message: this.$root.$t('notifications.wheel.duplicated'),
                type: 'success'
              })
              let wheelCreated = this.wheels.pop()
              this.wheels.splice(0, 0, wheelCreated)
            })
            .catch(response => {
              this.$notify.error({
                title: this.$root.$t('api.' + response.data.code + '.title'),
                message: this.$root.$t('api.' + response.data.code + '.message')
              })
            })
        })
        .catch(response => {
          this.$notify.error({
            title: this.$root.$t('api.' + response.data.code + '.title'),
            message: this.$root.$t('api.' + response.data.code + '.message')
          })
        })
    },

    mounted() {
      this.init()
    },
    closeTable() {
      for (let i = 0; i < this.wheels.length; ++i) {
        this.isModalOpen[i] = false
      }
      this.$forceUpdate()
    }
  }
}
</script>

<style>
.fields-container {
  display: flex;
  gap: 10px 16px;
  flex-flow: row wrap;
}

.button-field {
  padding: 4px 8px;
}

.copy-popin {
  display: none;
  z-index: 10;
  position: absolute;
  top: -36px;
  left: 50%;
  width: 70px;
  box-sizing: border-box;
  margin-left: -35px;
  padding: 4px 8px;
  text-align: center;
  border-radius: 0.25rem;
  background-color: #f59e0b;
  color: #fff;
}

.button-field.active ~ .copy-popin {
  display: block;
  animation: 1s linear pop;
  transform: scale(0);
}

@keyframes pop {
  from {
    transform: scale(0.5);
  }
  10% {
    transform: scale(1);
  }
  90% {
    transform: scale(1);
  }
  to {
    transform: scale(0.5);
  }
}
</style>



// WEBPACK FOOTER //
// src/components/wheels/ui/list.vue